import { Card } from "@/components/primitives";
import { usePricingDecisions, type PricingDecisionRow } from "@/hooks/usePricingDecisions";

function fmtMs(ms: number | null): string {
  if (ms == null) return "—";
  return `${(ms / 1000).toFixed(1)}s`;
}

function tierColor(tier: PricingDecisionRow["verdict_tier"]): string {
  if (tier === "Verified") return "hsl(var(--ink))";
  if (tier === "Likely Verified") return "hsl(var(--ink) / 0.6)";
  if (tier === "Not Eligible") return "hsl(0 60% 45%)";
  return "hsl(var(--ink) / 0.35)";
}

interface Props {
  /** When true, omits the page-level heading + helper text. Used by Admin.tsx
   *  tab embedding where the surrounding tab strip provides context. */
  embedded?: boolean;
}

export function PricingTelemetryPanel({ embedded = false }: Props) {
  const { data, isLoading, isError, error } = usePricingDecisions();
  const ink = "hsl(var(--ink))";

  return (
    <div style={{ color: ink }}>
      {!embedded && (
        <>
          <h1
            style={{
              fontFamily: "'Fraunces', serif",
              fontWeight: 500,
              fontSize: 24,
              margin: 0,
              marginBottom: 4,
            }}
          >
            Pricing telemetry
          </h1>
          <p style={{ fontSize: 12, opacity: 0.65, marginBottom: 16, lineHeight: 1.5 }}>
            Last 50 pricing decisions. Phase 1 starts populating this once the new MCP
            workflow is live; until then this view will be empty.
          </p>
        </>
      )}

      {isLoading && <p style={{ fontSize: 13 }}>Loading…</p>}
      {isError && (
        <p style={{ fontSize: 13, color: "hsl(0 70% 40%)" }}>
          Error: {error?.message}
        </p>
      )}
      {data && data.length === 0 && (
        <p style={{ fontSize: 13, fontStyle: "italic", opacity: 0.6 }}>
          No pricing decisions yet.
        </p>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {data?.map((row) => (
          <Card key={row.id}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: 6,
              }}
            >
              <span style={{ fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", opacity: 0.7 }}>
                Scan {row.scan_id ?? "(detached)"}
              </span>
              <span
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 10,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: tierColor(row.verdict_tier),
                  border: `1px solid ${tierColor(row.verdict_tier)}`,
                  padding: "2px 6px",
                }}
              >
                {row.verdict_tier}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                gap: 12,
                fontSize: 11,
                fontFamily: "'IBM Plex Mono', monospace",
                opacity: 0.65,
                marginBottom: 8,
              }}
            >
              <span>{new Date(row.created_at).toLocaleString()}</span>
              <span>duration {fmtMs(row.workflow_duration_ms)}</span>
              <span>mode {row.mode}</span>
              {row.partial_data && (
                <span style={{ color: "hsl(30 80% 40%)" }}>partial data</span>
              )}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 8,
                fontSize: 13,
                marginBottom: 6,
              }}
            >
              <div>
                <div style={{ fontSize: 10, opacity: 0.55, fontFamily: "'IBM Plex Mono', monospace" }}>
                  BAND
                </div>
                <div>
                  ${row.band_low ?? "—"} – ${row.band_high ?? "—"}
                  {row.band_suggested != null && (
                    <span style={{ opacity: 0.6 }}> (sug ${row.band_suggested})</span>
                  )}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, opacity: 0.55, fontFamily: "'IBM Plex Mono', monospace" }}>
                  BRAND TIER
                </div>
                <div>{row.brand_tier ?? "—"}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, opacity: 0.55, fontFamily: "'IBM Plex Mono', monospace" }}>
                  CONFIDENCE
                </div>
                <div>{row.confidence != null ? row.confidence.toFixed(2) : "—"}</div>
              </div>
            </div>
            {row.legacy_band_suggested != null && (
              <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>
                Legacy: ${row.legacy_band_low ?? "—"} – ${row.legacy_band_high ?? "—"}
                {" "}(sug ${row.legacy_band_suggested}){" "}
                {row.legacy_verdict_tier && `· tier ${row.legacy_verdict_tier}`}
                {row.midpoint_delta_pct != null && (
                  <span style={{ marginLeft: 6, opacity: 0.7 }}>
                    Δ {(row.midpoint_delta_pct * 100).toFixed(1)}%
                  </span>
                )}
              </div>
            )}
            {row.proto_judge_delta != null && (
              <div style={{ fontSize: 11, opacity: 0.6 }}>
                Proto-judge delta: {(row.proto_judge_delta * 100).toFixed(1)}%
              </div>
            )}
            <details style={{ fontSize: 11, marginTop: 6 }}>
              <summary style={{ cursor: "pointer", opacity: 0.65 }}>raw payload</summary>
              <pre
                style={{
                  marginTop: 6,
                  overflowX: "auto",
                  background: "hsl(var(--ink) / 0.05)",
                  padding: 8,
                  fontSize: 10,
                  fontFamily: "'IBM Plex Mono', monospace",
                }}
              >
                {JSON.stringify(
                  {
                    triage_result: row.triage_result,
                    premier_store: row.premier_store,
                    haiku_output: row.haiku_output,
                    sonnet_verdict: row.sonnet_verdict,
                    comps_used: row.comps_used,
                    reference_sections: row.reference_sections,
                    legacy_verdict: row.legacy_verdict,
                  },
                  null,
                  2,
                )}
              </pre>
            </details>
          </Card>
        ))}
      </div>
    </div>
  );
}
